/**
 * La ficha de un FOCO del mapa.
 *
 * Un foco del mapa de sanidad no es un adorno: sale de una o varias sesiones de
 * monitoreo reales —las que alguien registró desde el campo, con sus fotos, su
 * nota de voz y su clasificación—. Hasta ahora el pin no se podía tocar: se veía
 * que había «1» y ahí se acababa, había que ir a la página de plagas a buscar
 * cuál era.
 *
 * Se abre la MISMA hoja que en el listado de plagas (`ScoutingSessionDetailDrawer`),
 * a propósito: es la misma cosa mirada desde otro sitio, y una segunda ficha
 * parecida-pero-distinta es justo como divergen dos pantallas que deberían decir
 * lo mismo.
 *
 * Aquí solo vive la BÚSQUEDA: el foco guarda ids de sesión, y la hoja quiere la
 * fila entera con su nave, su sector y quién la inspeccionó.
 *
 * Y la hoja SE ABRE AL TOCAR, no cuando contesta la consulta (29-08-2026).
 * Esperar era lo que hacía que tocar el punto pareciera no hacer nada, y luego
 * la hoja subía tarde. Ahora sube al instante con un esqueleto del tamaño de lo
 * que va a caber, y el contenido aterriza dentro sin mover el marco: el SUELO
 * de `DETAIL_SHEET_FIT_CLASS` es lo que fija ese tamaño desde el primer
 * fotograma, para que no crezca a mitad de subida.
 *
 * El techo, en cambio, llega hasta arriba (01-09-2026): una sesión con nota de
 * voz, transcripción y fotos no cabe en 72dvh, y esta ficha es LA MISMA que se
 * abre desde el listado de plagas, donde sí se estiraba. Medir distinto según
 * por dónde se entre es justo lo que esta hoja compartida existe para evitar.
 */
import { Suspense, lazy, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { ScoutingSessionDetailDrawer } from "@/components/pests/ScoutingSessionDetailDrawer";
import { DETAIL_SHEET_FIT_CLASS } from "@/components/ui/sheetChrome";
import { useToast } from "@/hooks/use-toast";
import {
  buildTreatmentPrefill,
  type ScoutingPrefilledTaskData,
} from "@/lib/pests/treatmentPrefill";

/* En diferido: el formulario de tarea arrastra medio planificador y solo hace
   falta si se toca «Crear tratamiento». El mapa ya es de las pantallas más
   pesadas y no tiene por qué cargarlo para enseñar unas chinchetas. */
const TaskFormDialog = lazy(() => import("@/components/planner/TaskFormDialog"));

/** Lo que la hoja necesita de una sesión, con los nombres que ella usa. */
interface SessionRow {
  id: string;
  date: string;
  greenhouse_id: string | null;
  block_id: string | null;
  notes: string | null;
  created_at?: string;
  inspector_id?: string | null;
  greenhouse?: { name: string };
  block?: { name: string };
  inspector?: { full_name: string | null; avatar_url?: string | null };
}

export function useFocusSession(sessionId: string | null, companyId: string | undefined) {
  return useQuery({
    queryKey: ["focus-session", sessionId, companyId],
    enabled: !!sessionId && !!companyId,
    queryFn: async (): Promise<SessionRow | null> => {
      const { data, error } = await supabase
        .from("pest_scouting_sessions")
        .select(`id, date, notes, created_at, greenhouse_id, block_id, inspector_id,
                 greenhouse:greenhouses(name), block:blocks(name)`)
        .eq("id", sessionId!)
        .eq("company_id", companyId!)
        .maybeSingle();
      if (error || !data) return null;

      /* El inspector va en `profiles`, no colgando de la sesión: se pide aparte
         y si falla NO se tira la ficha entera — se queda sin nombre y ya. */
      let inspector: SessionRow["inspector"];
      if (data.inspector_id) {
        const { data: p } = await supabase
          .from("profiles")
          .select("full_name, avatar_url")
          .eq("user_id", data.inspector_id)
          .maybeSingle();
        if (p) inspector = { full_name: p.full_name, avatar_url: p.avatar_url ?? undefined };
      }
      return { ...(data as unknown as SessionRow), inspector };
    },
  });
}

export function FocusSessionSheet({
  sessionId,
  companyId,
  onClose,
  onChanged,
  currentFocusId = null,
  onOpenFocus,
}: {
  sessionId: string | null;
  companyId: string | undefined;
  onClose: () => void;
  /** Se ha tocado la sesión (clasificada, resuelta, borrada): refrescar el mapa. */
  onChanged?: () => void;
  /** El foco abierto debajo de esta ficha: si es el del parte, su fila no lleva botón. */
  currentFocusId?: string | null;
  /** Abrir la ficha de un foco en el propio mapa (un parte suelto que se acaba de clasificar). */
  onOpenFocus?: (focusId: string) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: session, isLoading } = useFocusSession(sessionId, companyId);
  /* «Crear tratamiento» ABRE LA VENTANA AQUÍ, no lleva a la página de plagas
     (petición del usuario 31-08-2026). Sacar a alguien del mapa para pedirle un
     tratamiento le costaba el sitio donde estaba mirando —qué nave, qué zoom,
     qué foco— y encima llegaba a un formulario vacío que tenía que rellenar a
     mano con lo que el parte ya sabía. */
  const [prefill, setPrefill] = useState<ScoutingPrefilledTaskData | null>(null);

  const openTreatment = async (s: {
    id: string;
    greenhouse_id: string | null;
    block_id: string | null;
    triage_pest_id?: string | null;
    triage_pest_name_snapshot?: string | null;
  }) => {
    const data = await buildTreatmentPrefill(s, t);
    if (!data) {
      toast({
        title: t("pests.scouting.errorNoBlock"),
        description: t("pests.scouting.errorNoBlockDesc"),
        variant: "destructive",
      });
      return;
    }
    /* La ficha se cierra sola al pulsar (lo hace el propio panel de triaje) y
       hay que ESPERAR a que su velo se vaya: abrir un diálogo de Radix mientras
       otro se cierra deja el `pointer-events: none` del cuerpo puesto y la
       ventana nueva sale muerta. Mismo retardo que en la página de plagas. */
    setTimeout(() => setPrefill(data), 200);
  };

  /* Ojo con el orden: el formulario NO puede colgar del `if (!sessionId)`. La
     ficha se cierra al pulsar «Crear tratamiento», así que para cuando la
     ventana se abre ya no hay foco abierto — si estuviera dentro del `return
     null` se desmontaría justo al aparecer. */
  return (
    <>
      {sessionId && (
        <ScoutingSessionDetailDrawer
          session={session ?? null}
          loading={isLoading}
          heightClassName={DETAIL_SHEET_FIT_CLASS}
          open
          onOpenChange={(open) => { if (!open) onClose(); }}
          onSessionUpdated={() => { onChanged?.(); }}
          onSessionDeleted={() => { onChanged?.(); onClose(); }}
          /* La ficha reenvía además `triage_pest_id` /
             `triage_pest_name_snapshot`, que su tipo no declara; da igual, el
             constructor del relleno los vuelve a leer de la base. */
          onCreateTreatment={(s) => { void openTreatment(s); }}
          currentFocusId={currentFocusId}
          onOpenFocus={onOpenFocus}
        />
      )}

      {prefill && (
        <Suspense fallback={null}>
          <TaskFormDialog
            open
            onOpenChange={(open) => { if (!open) setPrefill(null); }}
            prefilledData={prefill}
            onSuccess={() => {
              setPrefill(null);
              /* El parte pasa a «previsto»: el foco tiene que cambiar de color
                 sin que haya que recargar la página. */
              onChanged?.();
            }}
          />
        </Suspense>
      )}
    </>
  );
}
